import { DownloadOutlined } from '@ant-design/icons';
import {
    DatePicker,
    Form,
    Input,
    Select,
} from 'antd';
import type { FormInstance } from 'antd/es/form';
import { localize } from 'mixins/helpers';
import Button from 'mixins/ui/button/button';
import React, { FC } from 'react';
import { ApiServiceDictionaryItem } from '/react4xp/parts/admin-api-call-statistics/types';
import { DISPLAY_DATE_FORMAT } from '../utils/constants';
import { sanitizeTaxpayerCode } from '../utils/helpers';
import { CallStatisticsFilterValues } from '../utils/types';

const { RangePicker } = DatePicker;

interface CallStatisticsFilterProps {
    dictionary: ApiServiceDictionaryItem[];
    hasData: boolean;
    form: FormInstance<CallStatisticsFilterValues>;
    isExporting: boolean;
    isDictionaryLoading: boolean;
    isStatisticsLoading: boolean;
    translations: Record<string, string>;
    onExport: () => void;
    onReset: () => void;
    onSearch: (values: CallStatisticsFilterValues) => void;
    onTaxpayerCodeChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const CallStatisticsFilter: FC<CallStatisticsFilterProps> = ({
    dictionary,
    form,
    hasData,
    isDictionaryLoading,
    isExporting,
    isStatisticsLoading,
    onExport,
    onReset,
    onSearch,
    onTaxpayerCodeChange,
    translations,
}) => (
    <div className="admin-api-call-statistics__filters">
        <Form
            form={form}
            layout="vertical"
            onFinish={onSearch}
        >
            <div className="admin-api-call-statistics__filter-row admin-api-call-statistics__filter-row--top">
                <div className="admin-api-call-statistics__filter-field">
                    <div className="admin-api-call-statistics__field-label">
                        {localize('admin-api-stat-taxpayer-code', translations)}
                    </div>
                    <Form.Item
                        name="taxpayerCode"
                        normalize={sanitizeTaxpayerCode}
                        rules={[
                            {
                                validator: (_rule, value) => {
                                    if (!value || /^\d{12}$/.test(value)) {
                                        return Promise.resolve();
                                    }

                                    return Promise.reject(
                                        new Error(
                                            localize(
                                                'admin-api-stat-validation-taxpayer-code',
                                                translations
                                            )
                                        )
                                    );
                                },
                            },
                        ]}
                    >
                        <Input
                            maxLength={12}
                            inputMode="numeric"
                            placeholder={localize(
                                'admin-api-stat-taxpayer-placeholder',
                                translations
                            )}
                            onChange={onTaxpayerCodeChange}
                        />
                    </Form.Item>
                </div>

                <div className="admin-api-call-statistics__filter-field">
                    <div className="admin-api-call-statistics__field-label">
                        {localize('admin-api-stat-period', translations)}
                    </div>
                    <Form.Item
                        name="period"
                        rules={[
                            {
                                validator: (_rule, period: CallStatisticsFilterValues['period']) => {
                                    const [dateFrom, dateTo] = period || [];

                                    if (!dateFrom || !dateTo || !dateFrom.isAfter(dateTo, 'day')) {
                                        return Promise.resolve();
                                    }

                                    return Promise.reject(
                                        new Error(
                                            localize(
                                                'admin-api-stat-validation-date-range',
                                                translations
                                            )
                                        )
                                    );
                                },
                            },
                        ]}
                    >
                        <RangePicker
                            allowEmpty={[true, true]}
                            format={DISPLAY_DATE_FORMAT}
                            placeholder={[
                                localize('admin-api-stat-date-from-short', translations),
                                localize('admin-api-stat-date-to-short', translations),
                            ]}
                        />
                    </Form.Item>
                </div>
            </div>

            <div className="admin-api-call-statistics__filter-row admin-api-call-statistics__filter-row--bottom">
                <div className="admin-api-call-statistics__filter-field">
                    <div className="admin-api-call-statistics__field-label">
                        {localize('admin-api-stat-column-api', translations)}
                    </div>
                    <Form.Item name="apiCode">
                        <Select
                            allowClear
                            showSearch
                            loading={isDictionaryLoading}
                            optionFilterProp="label"
                            placeholder={localize('admin-api-stat-api-placeholder', translations)}
                            options={dictionary.map((item) => ({
                                value: item.code,
                                label: item.name,
                            }))}
                        />
                    </Form.Item>
                </div>

                <div className="admin-api-call-statistics__filter-actions">
                    <Form.Item className="admin-api-call-statistics__actions">
                        <Button
                            color="gold"
                            htmlType="submit"
                            loading={isStatisticsLoading}
                        >
                            {localize('admin-api-stat-search', translations)}
                        </Button>
                        <Button
                            htmlType="button"
                            onClick={onReset}
                        >
                            {localize('admin-api-stat-clear', translations)}
                        </Button>
                        <Button
                            htmlType="button"
                            icon={<DownloadOutlined />}
                            loading={isExporting}
                            disabled={!hasData || isStatisticsLoading || isDictionaryLoading}
                            onClick={onExport}
                        >
                            {localize('admin-api-stat-export-csv', translations)}
                        </Button>
                    </Form.Item>
                </div>
            </div>
        </Form>
    </div>
);